import { useEffect, useRef } from "react";
import { animate, motion, useMotionValue } from "framer-motion";
import { useGameStore } from "../store/useGameStore.js";
import { useCardSet } from "../contexts/CardSetContext.js";
import { useFlyAnimation } from "../contexts/FlyAnimationContext.js";
import { dragState, findDropTarget } from "../lib/dragState.js";

// ============================================================
// WastePile — Ablagestapel (Draw-1 und Draw-3)
// ============================================================

export function WastePile() {
  const { gameState, shakeCardId, handleWasteClick, applyDrop, drawMode } = useGameStore();
  const { getFaceUrl, getBackUrl, leftHanded } = useCardSet();
  const { isFlying } = useFlyAnimation();

  const waste = gameState.waste;
  const topCard = waste[waste.length - 1];

  // Draw-3: bis zu 3 Karten gefächert zeigen
  const isDraw3 = drawMode === 3;

  // Sichtbare Karten für Draw-3: letzte 3 (oder weniger falls Waste kleiner)
  const visibleCards = isDraw3
    ? waste.slice(Math.max(0, waste.length - 3))
    : topCard ? [topCard] : [];

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const isDraggingRef = useRef(false);
  const tapStartRef = useRef<{ x: number; y: number } | null>(null);

  // Ref: letzte sichtbare Karten-URL + Kartenanzahl. Wenn die neue Karte fliegt,
  // zeigen wir weiterhin die alte Karte statt sie zu verstecken.
  const stableFaceUrlRef = useRef<string>("");
  const stableCardCountRef = useRef<number>(0);

  // Ref aktualisieren wenn die oberste Karte NICHT fliegt (= sichtbar)
  if (topCard && !isFlying(topCard.id)) {
    stableFaceUrlRef.current = getFaceUrl(topCard);
    stableCardCountRef.current = visibleCards.length;
  } else if (!topCard) {
    // Waste leer → Refs zurücksetzen, damit beim nächsten Draw kein alter Wert flasht
    stableFaceUrlRef.current = "";
    stableCardCountRef.current = 0;
  }

  // Shake-Animation wenn shakeCardId auf die oberste Karte zeigt
  useEffect(() => {
    if (topCard && shakeCardId === topCard.id && !isDraggingRef.current) {
      animate(x, [0, -8, 8, -5, 5, 0], { duration: 0.4 });
    }
  }, [shakeCardId, topCard?.id, x]);

  if (visibleCards.length === 0) {
    return (
      <div
        className="relative w-full"
        style={{ aspectRatio: "2/3" }}
        aria-label="Ablagestapel leer"
      />
    );
  }

  function handleDragStart() {
    isDraggingRef.current = true;
    dragState.set({
      cards: [topCard!],
      fromPile: { kind: "waste" },
    });
  }

  function handleDragEnd(event: MouseEvent | TouchEvent | PointerEvent) {
    const clientX = "changedTouches" in event
      ? event.changedTouches[0]?.clientX ?? 0
      : (event as MouseEvent).clientX;
    const clientY = "changedTouches" in event
      ? event.changedTouches[0]?.clientY ?? 0
      : (event as MouseEvent).clientY;

    const target = findDropTarget(clientX, clientY);
    const success = target ? applyDrop(target) : false;

    if (!success) {
      dragState.clear();
      animate(x, 0, { type: "spring", stiffness: 500, damping: 35 });
      animate(y, 0, { type: "spring", stiffness: 500, damping: 35 }).then(() => {
        isDraggingRef.current = false;
      });
    } else {
      x.set(0);
      y.set(0);
      isDraggingRef.current = false;
    }
  }

  // Bild-URL für die oberste Karte: zeigt alte Karte solange neue fliegt
  const topFlyingNow = topCard && isFlying(topCard.id);
  const topImgUrl = topFlyingNow
    ? stableFaceUrlRef.current || getFaceUrl(topCard!)
    : getFaceUrl(topCard!);

  // Draw-1 Modus: einfache Ansicht — mit Below-Card die beim Draggen/Flying sichtbar wird
  const belowCard = !isDraw3 && waste.length >= 2 ? waste[waste.length - 2] : null;

  if (!isDraw3) {
    return (
      <div
        className="relative w-full"
        style={{ aspectRatio: "2/3" }}
        onPointerDownCapture={(e) => {
          tapStartRef.current = { x: e.clientX, y: e.clientY };
        }}
        onPointerUpCapture={(e) => {
          const start = tapStartRef.current;
          tapStartRef.current = null;
          if (!start) return;
          if (isDraggingRef.current) return;
          if (Math.abs(e.clientX - start.x) > 5 || Math.abs(e.clientY - start.y) > 5) return;
          const cardEl = (e.target as HTMLElement).closest("[data-card-id]");
          if (cardEl) {
            const rect = cardEl.getBoundingClientRect();
            const insetX = 6, insetY = 4;
            if (
              e.clientX < rect.left + insetX || e.clientX > rect.right - insetX ||
              e.clientY < rect.top + insetY || e.clientY > rect.bottom - insetY
            ) return;
          }
          handleWasteClick();
        }}
      >
        {/* Below-Card: sichtbar wenn die Top-Karte weggedraggt oder geflogen wird */}
        {belowCard && (
          <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 0 }}>
            <img
              src={getFaceUrl(belowCard)}
              alt={`${belowCard.rank} of ${belowCard.suit}`}
              className="w-full h-full object-fill rounded-md block"
              draggable={false}
            />
          </div>
        )}
        <motion.div
          data-card-id={topCard!.id}
          className="relative w-full cursor-pointer"
          style={{
            aspectRatio: "2/3",
            x,
            y,
            zIndex: 1,
            // Verstecken wenn die Karte fliegt UND kein alter Zustand vorhanden (z.B. erster Draw bei leerem Waste)
            visibility: (topFlyingNow && !stableFaceUrlRef.current) ? "hidden" : undefined,
          }}
          drag
          dragMomentum={false}
          dragElastic={0.05}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          whileDrag={{ zIndex: 50, scale: 1.03 }}
          role="button"
          aria-label={`Ablagestapel: ${topCard!.rank} of ${topCard!.suit}`}
        >
          <img
            src={topImgUrl}
            alt={`${topCard!.rank} of ${topCard!.suit}`}
            className="w-full h-full object-fill rounded-md block pointer-events-none"
            draggable={false}
          />
        </motion.div>
      </div>
    );
  }

  // Draw-3 Modus: gefächerte Ansicht
  // Fächer-Richtung: normal → rechts, leftHanded → links
  const fanDirection = leftHanded ? -1 : 1;

  return (
    <div
      className="relative w-full"
      style={{ aspectRatio: "2/3" }}
      onPointerDownCapture={(e) => {
        tapStartRef.current = { x: e.clientX, y: e.clientY };
      }}
      onPointerUpCapture={(e) => {
        const start = tapStartRef.current;
        tapStartRef.current = null;
        if (!start) return;
        if (isDraggingRef.current) return;
        if (Math.abs(e.clientX - start.x) > 5 || Math.abs(e.clientY - start.y) > 5) return;
        const cardEl = (e.target as HTMLElement).closest("[data-card-id]");
        if (cardEl) {
          const rect = cardEl.getBoundingClientRect();
          const insetX = 6, insetY = 4;
          if (
            e.clientX < rect.left + insetX || e.clientX > rect.right - insetX ||
            e.clientY < rect.top + insetY || e.clientY > rect.bottom - insetY
          ) return;
        }
        handleWasteClick();
      }}
    >
      {/* Stabiler Hintergrund — zeigt den ALTEN Zustand während die neuen Karten fliegen.
          Kartenrücken bei z=4, offene Karte bei z=5 → neue Karten (z=10+) decken sie ab. */}
      {topFlyingNow && stableFaceUrlRef.current && (
        <>
          {Array.from({ length: Math.max(0, stableCardCountRef.current - 1) }, (_, i) => (
            <div
              key={`stable-back-${i}`}
              className="absolute inset-0 pointer-events-none"
              style={{
                left: `${i * 30 * fanDirection}%`,
                width: "100%",
                aspectRatio: "2/3",
                zIndex: 4 + i,
              }}
            >
              <img
                src={getBackUrl()}
                alt=""
                className="w-full h-full object-fill rounded-md block"
                draggable={false}
              />
            </div>
          ))}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              left: `${(stableCardCountRef.current - 1) * 30 * fanDirection}%`,
              width: "100%",
              aspectRatio: "2/3",
              zIndex: 4 + stableCardCountRef.current,
            }}
          >
            <img
              src={stableFaceUrlRef.current}
              alt=""
              className="w-full h-full object-fill rounded-md block"
              draggable={false}
            />
          </div>
        </>
      )}

      {/* Karten gefächert: von unten nach oben */}
      {visibleCards.map((card, idx) => {
        const isTop = idx === visibleCards.length - 1;
        // Offset: 0%, 30%, 60% der Kartenbreite
        const offsetPercent = idx * 30 * fanDirection;

        if (isTop) {
          // Oberste Karte: aufgedeckt, draggbar, klickbar
          // Im Draw-3 während Fly: hidden (stabile Karte zeigt Hintergrund bei z=5)
          return (
            <motion.div
              key={card.id}
              data-card-id={card.id}
              className="absolute inset-0 cursor-pointer"
              style={{
                left: `${offsetPercent}%`,
                width: "100%",
                aspectRatio: "2/3",
                x,
                y,
                zIndex: 10 + idx,
                visibility: topFlyingNow ? "hidden" : undefined,
              }}
              drag
              dragMomentum={false}
              dragElastic={0.05}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              whileDrag={{ zIndex: 50, scale: 1.03 }}
              role="button"
              aria-label={`Ablagestapel: ${card.rank} of ${card.suit}`}
            >
              <img
                src={topImgUrl}
                alt={`${card.rank} of ${card.suit}`}
                className="w-full h-full object-fill rounded-md block pointer-events-none"
                draggable={false}
              />
            </motion.div>
          );
        }

        // Darunter liegende Karten: Kartenrücken, nicht interaktiv
        // data-card-id nötig damit FlyAnimation das Ziel-Rect findet
        // visibility hidden solange die Karte fliegt → erscheint erst wenn Batch landet
        return (
          <div
            key={card.id}
            data-card-id={card.id}
            className="absolute inset-0 pointer-events-none"
            style={{
              left: `${offsetPercent}%`,
              width: "100%",
              aspectRatio: "2/3",
              zIndex: 10 + idx,
              visibility: isFlying(card.id) ? "hidden" : undefined,
            }}
          >
            <img
              src={getBackUrl()}
              alt="Card back"
              className="w-full h-full object-fill rounded-md block"
              draggable={false}
            />
          </div>
        );
      })}
    </div>
  );
}
